import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { Flower2, Leaf, Sparkles } from "lucide-react";
import { COVER_URL } from "./constants";

const FloatingBit = ({ className, delay, children }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    className={`absolute ${className}`}
  >
    <div className="animate-float-slow">{children}</div>
  </motion.div>
);

export const BookTilt = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [9, -9]), {
    stiffness: 110,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-11, 11]), {
    stiffness: 110,
    damping: 20,
  });
  const sheenX = useTransform(mx, [0, 1], [-120, 120]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  const onLeave = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative flex items-center justify-center"
      style={{ perspective: 1200 }}
      data-testid="hero-3d-journal-card"
    >
      <div className="spotlight absolute -inset-24 rounded-full" />

      <motion.div
        animate={{ y: [0, -12, 0] }}
        transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <motion.div
          initial={{ opacity: 0, y: 70, rotateY: -24 }}
          animate={{ opacity: 1, y: 0, rotateY: 0 }}
          transition={{ duration: 1.4, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
          className="relative"
        >
          <div
            className="absolute -left-2.5 top-[2%] h-[96%] w-3 rounded-l-sm bg-gradient-to-b from-siennadeep via-sienna to-espresso"
            style={{ transform: "translateZ(-6px) rotateY(-18deg)" }}
          />
          <div
            className="absolute -right-1.5 top-[1.5%] h-[97%] w-2 bg-gradient-to-r from-[#f3ead9] to-[#d9cdb8]"
            style={{ transform: "translateZ(-3px)" }}
          />
          <img
            src={COVER_URL}
            alt="Cover of the Love the Life You Live gratitude journal"
            className="book-shadow relative w-56 rounded-r-md rounded-l-[2px] sm:w-72 lg:w-80"
            draggable={false}
          />
          <motion.div
            style={{ x: sheenX }}
            className="pointer-events-none absolute inset-0 overflow-hidden rounded-r-md"
          >
            <div className="absolute inset-y-0 left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent blur-md" />
          </motion.div>
        </motion.div>
      </motion.div>

      <FloatingBit className="-left-8 top-10 sm:-left-16" delay={1.3}>
        <Flower2 size={44} className="text-terracotta/60" strokeWidth={1.2} />
      </FloatingBit>
      <FloatingBit className="-right-6 bottom-16 sm:-right-14" delay={1.5}>
        <Leaf size={38} className="text-sage/70" strokeWidth={1.2} />
      </FloatingBit>
      <FloatingBit className="-top-6 right-10" delay={1.7}>
        <Sparkles size={30} className="text-gold/70" strokeWidth={1.2} />
      </FloatingBit>
    </div>
  );
};
